import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_INFERNO } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantMergeBeatsWithSpacing,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/hollah.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/hollah.mp3',
  attribution: '"Hollah!" by Disfigure [NCS Release] - https://ncs.io/Hollah',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('hollah', variant),
    slug: buildVariantSlug('hollah', variant),
    title: buildVariantTitle('Hollah!', variant),
    description:
      'Bouncy drumstep at 140 BPM. Tight syncopation and a lead-heavy second half push this firmly into hard territory.',
    bpm: onsets.bpm,
    difficulty: 4,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_INFERNO,
    audio,
  });

  // 60s @ 140 BPM ≈ 140 beats. The cut opens cold on the verse riff, lands
  // in the main drop at beat 16, breaks down at 64, then rides the second
  // drop through to the end.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 64, kind: 'drop' });
  builder.section({ startBeat: 64, endBeat: 80, kind: 'bridge' });
  builder.section({ startBeat: 80, endBeat: 140, kind: 'drop' });

  // Verse: snare + the strongest leads. Hollah's leads sit overwhelmingly
  // at <0.15 intensity (synth shimmer / hi-hats); leads are detector-capped
  // at 0.25 so 0.18+ catches just the riff peaks while snare carries the
  // pulse.
  const versePads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['snare', 'lead'],
    minIntensity: 0.18,
    minSpacing: 0.75,
  });

  // First drop: dense lead riff + snare backbeat. 0.5-beat global floor at
  // 140 BPM = 214ms - same playability target as Heroes' second drop.
  const drop1 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['snare'],
      minIntensity: 0.25,
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['lead'],
      minIntensity: 0.2,
      minSpacing: 0.5,
    }),
  );

  // Break: lead-only melodic interlude. Lower density, players catch their
  // breath before the second drop.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 80,
    kinds: ['lead'],
    minIntensity: 0.18,
    minSpacing: 1,
  });

  // Second drop: kicks finally show up here in volume; merge them in for
  // the climax. Same 0.5-beat floor.
  const drop2 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 80,
      untilBeat: 138,
      kinds: ['kick', 'snare'],
      minIntensity: 0.25,
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 80,
      untilBeat: 138,
      kinds: ['lead'],
      minIntensity: 0.2,
      minSpacing: 0.5,
    }),
  );

  const padBeats = mergeBeats(versePads, drop1, bridgePads, drop2);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // 140 BPM ≈ 429ms per beat. minGap 7 ≈ 3 sec of silence before opening a
  // chamber - plenty of breathing room without inserting one mid-phrase.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 138,
    minGapBeats: 7,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(140);
  return builder.build();
}

export const HOLLAH_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const HOLLAH_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(HOLLAH_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
